import { useEffect, useRef, useState } from 'react';
import { IconCloudCheck, IconCloudOff, IconCloudUpload, IconX } from '@tabler/icons-react';
import { clearProblem, useSyncStore } from '@/lib/syncQueue';

type Phase = 'idle' | 'saving' | 'waiting' | 'saved';

/**
 * Small sync status band under the header. Silent when nothing is waiting.
 *   waiting  "Saved on this phone, will sync" (no signal, or the last try failed)
 *   saving   "Saving..."
 *   saved    "All saved" for a few seconds after the queue empties
 * Plus a one-off message when the server refused a change. The region is aria-live so screen readers
 * hear the changes.
 */
export function SyncBar({ hideStatus }: { hideStatus?: boolean }) {
  const count = useSyncStore((s) => s.entries.length);
  const online = useSyncStore((s) => s.online);
  const failing = useSyncStore((s) => s.entries.some((e) => e.attempts > 0));
  const problem = useSyncStore((s) => s.problem);
  const [showSaved, setShowSaved] = useState(false);
  const hadEntries = useRef(count > 0);

  useEffect(() => {
    if (count > 0) {
      hadEntries.current = true;
      setShowSaved(false);
      return undefined;
    }
    if (!hadEntries.current) return undefined;
    hadEntries.current = false;
    setShowSaved(true);
    const t = window.setTimeout(() => setShowSaved(false), 3000);
    return () => window.clearTimeout(t);
  }, [count]);

  let phase: Phase = 'idle';
  if (count > 0) phase = !online || failing ? 'waiting' : 'saving';
  else if (showSaved) phase = 'saved';

  return (
    <div className="proc-syncregion" role="status" aria-live="polite">
      {phase !== 'idle' && !hideStatus && (
        <div className={`proc-syncbar ${phase}`}>
          <span className="ic" aria-hidden="true">
            {phase === 'waiting' ? <IconCloudOff size={18} /> : phase === 'saving' ? <IconCloudUpload size={18} /> : <IconCloudCheck size={18} />}
          </span>
          <span className="msg">
            {phase === 'waiting' && (
              <>Saved on this phone, will sync{count > 1 ? ` (${count} changes)` : ''}</>
            )}
            {phase === 'saving' && 'Saving...'}
            {phase === 'saved' && 'All saved'}
          </span>
        </div>
      )}
      {problem && (
        <div className="proc-syncbar problem">
          <span className="msg">{problem.message}</span>
          <button type="button" className="proc-iconbtn sm" aria-label="Dismiss this message" onClick={clearProblem}>
            <IconX size={18} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Compact cloud icon for the header while something is waiting (the band under the header says why). */
export function SyncIndicator() {
  const count = useSyncStore((s) => s.entries.length);
  const online = useSyncStore((s) => s.online);
  if (count === 0) return null;
  return (
    <span className="proc-syncdot" title={online ? 'Saving' : 'Saved on this phone, will sync'} aria-hidden="true">
      {online ? <IconCloudUpload size={20} /> : <IconCloudOff size={20} />}
    </span>
  );
}
